import { useCrud } from './useCrud';
import { STORAGE_KEYS } from '../models/constants';
import { 
  sortTransactionsByDate,
  filterTransactionsByType,
  filterTransactionsByDateRange,
  getTransactionSummary,
  TRANSACTION_TYPES
} from '../models/TransactionHistory';

/**
 * Transaction-specific CRUD hook with localStorage persistence
 */
export const useTransactions = (initialData = []) => {
  // Load initial data from localStorage if available
  const loadInitialData = () => {
    if (initialData.length > 0) return initialData;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TRANSACTIONS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading transactions from localStorage:', error);
      return [];
    }
  };

  // Generate transaction ID
  const generateTransactionId = () => {
    return 'txn_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const transactionCrud = useCrud(loadInitialData(), generateTransactionId);

  // Override create to save to localStorage
  const createTransaction = async (transactionData) => {
    try {
      const newTransaction = transactionCrud.create(transactionData);
      return { success: true, data: newTransaction };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Override update to save to localStorage
  const updateTransaction = async (id, updates) => {
    try {
      transactionCrud.update(id, updates);
      saveToStorage(transactionCrud.items);
      return { success: true, data: transactionCrud.getById(id) };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Override remove to save to localStorage
  const removeTransaction = (id) => {
    try {
      transactionCrud.remove(id);
      saveToStorage(transactionCrud.items);
      return { success: true };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Save to localStorage
  const saveToStorage = (transactions) => {
    try {
      localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(transactions));
    } catch (error) {
      console.error('Error saving transactions to localStorage:', error);
    }
  };

  // Get transactions by client ID
  const getTransactionsByClientId = (clientId) => {
    return transactionCrud.items.filter(transaction => 
      transaction.clientId == clientId || 
      transaction.clientId === String(clientId) || 
      String(transaction.clientId) === String(clientId)
    );
  };

  // Get transactions sorted by date (most recent first)
  const getTransactionsSortedByDate = (clientId = null) => {
    const transactions = clientId 
      ? getTransactionsByClientId(clientId)
      : transactionCrud.items;
    return sortTransactionsByDate(transactions);
  };

  // Get transactions by type
  const getTransactionsByType = (type, clientId = null) => {
    const transactions = clientId 
      ? getTransactionsByClientId(clientId)
      : transactionCrud.items;
    return filterTransactionsByType(transactions, type);
  };

  // Get transactions by date range
  const getTransactionsByDateRange = (startDate, endDate, clientId = null) => {
    const transactions = clientId 
      ? getTransactionsByClientId(clientId)
      : transactionCrud.items;
    return filterTransactionsByDateRange(transactions, startDate, endDate);
  };

  // Get transaction summary for a client
  const getClientTransactionSummary = (clientId) => {
    const clientTransactions = getTransactionsByClientId(clientId);
    return getTransactionSummary(clientTransactions);
  };

  // Get payment transactions
  const getPaymentTransactions = (clientId = null) => {
    return getTransactionsByType(TRANSACTION_TYPES.PAYMENT, clientId);
  };

  // Get contact transactions
  const getContactTransactions = (clientId = null) => {
    return getTransactionsByType(TRANSACTION_TYPES.CONTACT, clientId);
  };

  // Get adjustment transactions
  const getAdjustmentTransactions = (clientId = null) => {
    return getTransactionsByType(TRANSACTION_TYPES.ADJUSTMENT, clientId);
  };

  // Get recent transactions (last N transactions)
  const getRecentTransactions = (limit = 10, clientId = null) => {
    const sortedTransactions = getTransactionsSortedByDate(clientId);
    return sortedTransactions.slice(0, limit);
  };

  // Get transactions for today
  const getTodaysTransactions = (clientId = null) => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    
    return getTransactionsByDateRange(startOfDay, endOfDay, clientId);
  };

  // Get transaction statistics
  const getTransactionStatistics = (clientId = null) => {
    const transactions = clientId 
      ? getTransactionsByClientId(clientId)
      : transactionCrud.items;

    const stats = {
      total: transactions.length,
      payments: 0,
      adjustments: 0,
      contacts: 0,
      arrangements: 0,
      totalPaymentAmount: 0,
      totalAdjustmentAmount: 0,
      lastTransactionDate: null
    };

    transactions.forEach(transaction => {
      switch (transaction.type) {
        case TRANSACTION_TYPES.PAYMENT:
          stats.payments++;
          stats.totalPaymentAmount += transaction.amount || 0;
          break;
        case TRANSACTION_TYPES.ADJUSTMENT:
          stats.adjustments++;
          stats.totalAdjustmentAmount += transaction.amount || 0;
          break;
        case TRANSACTION_TYPES.CONTACT:
          stats.contacts++;
          break;
        case TRANSACTION_TYPES.ARRANGEMENT:
          stats.arrangements++;
          break;
      }

      if (!stats.lastTransactionDate || transaction.timestamp > stats.lastTransactionDate) {
        stats.lastTransactionDate = transaction.timestamp;
      }
    });

    return stats;
  };

  // Search transactions by description
  const searchTransactionsByDescription = (searchTerm, clientId = null) => {
    const transactions = clientId 
      ? getTransactionsByClientId(clientId)
      : transactionCrud.items;

    if (!searchTerm || searchTerm.trim().length === 0) {
      return transactions;
    }

    const term = searchTerm.toLowerCase().trim();
    return transactions.filter(transaction => 
      transaction.description.toLowerCase().includes(term) ||
      (transaction.reference && transaction.reference.toLowerCase().includes(term))
    );
  };

  return {
    ...transactionCrud,
    create: createTransaction,
    update: updateTransaction,
    remove: removeTransaction,
    getTransactionsByClientId,
    getTransactionsSortedByDate,
    getTransactionsByType,
    getTransactionsByDateRange,
    getClientTransactionSummary,
    getPaymentTransactions,
    getContactTransactions,
    getAdjustmentTransactions,
    getRecentTransactions,
    getTodaysTransactions,
    getTransactionStatistics,
    searchTransactionsByDescription,
    saveToStorage: () => saveToStorage(transactionCrud.items) // Explicit save using current state
  };
};